import {
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import _ from 'lodash';
import moment from 'moment';
import t from 'react-translate';

// Redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { IntegrationAuth, MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { getMentorshipProgramSessionPlan, getSessionActionItems } from 'redux/selectors/mentorship-program-sessions';

// Styles
import { css } from '@emotion/react';
import { gray1, gray2, gray5, gray6 } from 'styles/global_defaults/colors';
import {
  semiBoldFontWeight,
  textLargeFontSize,
  textMediumLineHeight,
  textMediumFontSize,
  textSmallLineHeight,
  headerLineHeight,
} from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { screenXsMax } from 'styles/global_defaults/media-queries';

// Components
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import useConnection from 'athena/hooks/use-connection';
import SessionEditMode from './session-edit-mode/session-edit-mode';
import SessionViewMode from './session-view-mode/session-view-mode';
import { removeDeletedActionItem } from 'redux/actions/mentoring-program-action-items';
import { deleteMentoringProgramSession, resetMentoringProgramSessionsList } from 'redux/actions/mentoring-program-sessions';
import { MentoringProgramActionItem } from 'redux/schemas/models/mentoring-program-action-items';


export const DEFAULT_DURATION = 30;

const styles = (
  isEdit: boolean,
  viewMode: boolean,
  screenWidth: number,
) => css`
  height: 100%;
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    justify-content: space-between;
    padding: ${standardSpacing}px ${largeSpacing}px;
    font-weight: ${semiBoldFontWeight};
    font-size: ${textLargeFontSize}px;
    line-height: ${textMediumLineHeight}px;
    border-bottom: 1px solid ${gray5};
  }

  .content {
    flex: 1;
    ${viewMode &&
    css`
      border-top: 1px solid ${gray5};
    `}
    padding: ${standardSpacing}px ${largeSpacing}px;
    top: ${tripleSpacing}px;
    bottom: ${isEdit ? 0 : 107}px;
    overflow-y: auto;

    .session-title, .session-time, .session-action-items, .session-details {
      color: ${gray2};
      font-weight: ${semiBoldFontWeight};
      font-size: ${textMediumFontSize}px;
      line-height: ${textSmallLineHeight}px;
    }

    .session-time {
      color: ${gray1};
    }

    .title {
      margin-bottom: ${threeQuartersSpacing}px;
    }
    .bs4-dropdown {
      .title {
        margin-bottom: unset;
      }
    }

    .date-time {
      & > div, .react-datepicker-wrapper {
        width: 100%;
      }

      width: 100%;
      padding-bottom: ${largeSpacing}px;
    }

    .session-action-items {
      padding-top: ${standardSpacing}px;
    }

    .session-details {
      padding-top: ${standardSpacing}px;
      border-top: 1px solid ${gray5};
    }

    .session-details-description {
      color: ${gray1};
      line-height: ${headerLineHeight}px;
      padding-top: ${quarterSpacing}px;
      padding-bottom: ${halfSpacing}px;
    }
  }

  form {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .button-row {
    bottom: 0;
    width: ${screenWidth}px;
    padding: ${largeSpacing}px;
    text-align: right;
    border-top: 1px solid ${gray6};

    button {
      margin-left: ${threeQuartersSpacing}px;
    }
  }
`;

const FLYOUT_MODAL_WIDTH = 640;

const getInitialSessionDraft = (session: MentorshipProgramSession, defaultTitle) => ({
  title: session?.title ?? defaultTitle ?? '',
  scheduledDate: session?.scheduledDate ? moment(session.scheduledDate) : null,
  details: session?.details ?? '',
  sessionType: session?.liveSession?.[0].sessionType ?? null,
  joinUrl: session?.liveSession?.[0].joinUrl ?? null,
  ...session,
});

type PlanSessionFlyoutModalProps = {
  onClose: () => void;
  session?: MentorshipProgramSession;
  editMode?: boolean;
  createMeetingPlan?: boolean;
  integrations?: IntegrationAuth;
  shouldShowPulseCheckCTA: boolean;
  openSessionPulseCheck: (sessionId: number) => void;
  openIntegrationsModal?: () => void;
};

const PlanSessionFlyoutModal = forwardRef((props: PlanSessionFlyoutModalProps, ref) => {
  const {
    onClose,
    session,
    editMode = false,
    createMeetingPlan = false,
    integrations,
    shouldShowPulseCheckCTA,
    openSessionPulseCheck,
    openIntegrationsModal,
  } = props;
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { mentorshipProgramEnrollment: enrollmentId } = mentoringProgram;
  const dispatch = useAppDispatch();
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT;
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { connection, mentorshipProgramEnrollment } = useConnection(enrollmentId);
  const { user: connectionUser } = connection?.assignedEnrollment || {};
  const { user: currentUser } = mentorshipProgramEnrollment || {};

  const meetingPlan = useSelector(
    getMentorshipProgramSessionPlan(connection?.connectionId),
  );

  const defaultTitle = translationBase.DEFAULT_TITLE(currentUser.firstName, connectionUser.firstName);
  const [sessionDraft, setSessionDraft] = useState<MentorshipProgramSession>(
    getInitialSessionDraft(session, defaultTitle),
  );
  const isEdit = !!session?.id || !!sessionDraft?.id;
  const [viewMode, setViewMode] = useState<boolean>(!editMode && isEdit);
  const [isTablet, setIsTablet] = useState(document.body.clientWidth <= screenXsMax);
  const [flyoutModalWidth, setFlyoutModalWidth] = useState(isTablet ? document.body.clientWidth : FLYOUT_MODAL_WIDTH);

  const handleSubmit = (data) => {
    setSessionDraft(getInitialSessionDraft(data, data?.title));
  };

  const handleClose = useCallback((shouldCloseModal = true) => {
    const dialogOptions = {
      title: translationBase.CONFIRMATION_MODAL.HEADER(),
      bodyText: translationBase.CONFIRMATION_MODAL.BODY(),
      confirmText: t.FORM.DISCARD(),
    };

    if (isFormDirty) {
      dispatch(
        openConfirmationDialog({
          ...dialogOptions,
          onConfirm: () => {
            if (shouldCloseModal || !isEdit) onClose();
            else {
              setIsFormDirty(false);
              setViewMode(true);
            }
          },
          onCancel: () => {
            if (isEdit && shouldCloseModal) {
              setIsFormDirty(false);
              setViewMode(true);
            }
          },
        }),
      );
      return;
    }

    if (isEdit) {
      if (shouldCloseModal || viewMode) onClose();
      else setViewMode(true);
      return;
    }

    onClose();
  }, [isFormDirty, isEdit, viewMode, onClose]);

  const handleSessionUpdated = () => {
    setIsFormDirty(false);
    setViewMode(true);
  };

  const handleSessionCreated = () => {
    setIsFormDirty(false);
    if (isEdit || createMeetingPlan) onClose();
    else setViewMode(true);
  };

  const handleViewModeUpdated = (session: MentorshipProgramSession) => {
    setSessionDraft(session);
  };

  const sessionActionItems: MentoringProgramActionItem[] = useSelector(getSessionActionItems(session?.id));

  const deleteSession = () => {
    dispatch(openConfirmationDialog({
      title: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.DELETE_THIS_SESSION(),
      bodyText: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.DELETE_DESCRIPTION(),
      confirmText: t.FORM.DELETE(),
      onConfirm: () => {
        dispatch(deleteMentoringProgramSession({
          programId: mentoringProgram.id,
          sessionId: session?.id,
          enrollmentId,
        })).then(() => {
          sessionActionItems?.forEach(item => (
            dispatch(removeDeletedActionItem({
              connectionId: connection?.connectionId,
              actionItemId: item.id,
            }))
          ));
          dispatch(resetMentoringProgramSessionsList({ resetList: true }));
          handleClose();
        });
      },
    }));
  };

  useEffect(() => {
    const handleResize = () => {
      const isNowTablet = document.body.clientWidth <= screenXsMax;
      setIsTablet(isNowTablet);
      setFlyoutModalWidth(isNowTablet ? document.body.clientWidth : FLYOUT_MODAL_WIDTH);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Fragment>
      <NvFlyoutModal
        type={ModalType.RIGHT}
        width={flyoutModalWidth}
        onClose={handleClose}
      >
        <div
          css={styles(isEdit, viewMode, flyoutModalWidth)}
          className='position-relative'
        >
          {viewMode ? (
            <SessionViewMode
              session={sessionDraft}
              onUpdateSession={handleViewModeUpdated}
              connection={connection}
              setViewMode={setViewMode}
              shouldShowPulseCheckCTA={shouldShowPulseCheckCTA}
              openSessionPulseCheck={openSessionPulseCheck}
              integrations={integrations}
              openIntegrationsModal={openIntegrationsModal}
              deleteSession={deleteSession}
            />
          ) : (
            <SessionEditMode
              ref={ref}
              session={sessionDraft}
              isEdit={isEdit}
              createMeetingPlan={createMeetingPlan}
              meetingPlan={meetingPlan}
              onClose={handleClose}
              onSessionCreated={handleSessionCreated}
              onSessionUpdated={handleSessionUpdated}
              onSubmit={handleSubmit}
              onFormDirtyChange={setIsFormDirty}
              integrations={integrations}
              openIntegrationsModal={openIntegrationsModal}
              deleteSession={deleteSession}
            />
          )}
        </div>
      </NvFlyoutModal>
    </Fragment>
  );
});

export default PlanSessionFlyoutModal;
